import { memo, useLayoutEffect, useRef } from "react";
import * as THREE from "three";
import { LED_POINT_DISC_RADIUS } from "../geometry";
import type { LedPointSample } from "../geometry";
import type { ShaderCompileParams } from "../rgbWheelShader";

const _m = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _pos = new THREE.Vector3();
const _scale = new THREE.Vector3(1, 1, 1);

interface Props {
  points: LedPointSample[];
  /** Z position shared by every point in this layer. */
  z: number;
  /** Solid LED tint (used when `rgb === false`). */
  color: string;
  /** RGB-wheel mode. */
  rgb: boolean;
  /** Per-layer opacity (infinityFade). */
  opacity: number;
  renderOrder: number;
  onBeforeCompileSolid: (shader: ShaderCompileParams) => void;
  onBeforeCompileBasic: (shader: ShaderCompileParams) => void;
}

/**
 * One InstancedMesh per depth layer for LED point lights. Replaces the
 * earlier per-point `<mesh>` map (≤140 points × ≤11 layers = up to 1540
 * meshes / draw calls) with a single instanced draw call per layer, so the
 * total drops to `≤ depthLayers`.
 *
 * Each instance carries the radial-inward orientation that the original
 * non-instanced version composed per-mesh — see the quaternion math below.
 */
export const LedPointsInstancedLayer = memo(function LedPointsInstancedLayer({
  points,
  z,
  color,
  rgb,
  opacity,
  renderOrder,
  onBeforeCompileSolid,
  onBeforeCompileBasic,
}: Props) {
  const ref = useRef<THREE.InstancedMesh>(null);
  const count = points.length;

  useLayoutEffect(() => {
    const mesh = ref.current;
    if (!mesh) return;
    /**
     * Default circle normal is +Z. Rotate each instance so its face is flush
     * against the side wall, with the normal pointing radially inward
     * (`p.nx`, `p.ny`). The rotation is π/2 around axis (-p.ny, p.nx, 0);
     * sin(π/4) = cos(π/4) = √½. Matches the pre-instanced quaternion.
     */
    const s = Math.SQRT1_2;
    for (let i = 0; i < count; i++) {
      const p = points[i];
      _pos.set(p.x, p.y, z);
      const hasN = p.nx * p.nx + p.ny * p.ny > 0.5;
      if (hasN) _q.set(-p.ny * s, p.nx * s, 0, s);
      else _q.identity();
      _m.compose(_pos, _q, _scale);
      mesh.setMatrixAt(i, _m);
    }
    mesh.instanceMatrix.needsUpdate = true;
  }, [points, z, count]);

  if (count === 0) return null;

  return (
    <instancedMesh
      ref={ref}
      key={count}
      args={[undefined, undefined, count]}
      renderOrder={renderOrder}
    >
      <circleGeometry args={[LED_POINT_DISC_RADIUS, 12]} />
      {rgb ? (
        <meshBasicMaterial
          key="rgb"
          color="#ffffff"
          side={THREE.DoubleSide}
          toneMapped={false}
          transparent
          opacity={opacity}
          depthWrite={false}
          defines={{ USE_RGB_WHEEL_LED_BASIC: 1 }}
          onBeforeCompile={onBeforeCompileBasic}
        />
      ) : (
        <meshBasicMaterial
          key="solid"
          color={color}
          side={THREE.DoubleSide}
          toneMapped={false}
          transparent
          opacity={opacity}
          depthWrite={false}
          defines={{ USE_LED_GAIN_SOLID: 1 }}
          onBeforeCompile={onBeforeCompileSolid}
        />
      )}
    </instancedMesh>
  );
});
